<template>
  <div class="post-editor">
    <header class="editor-header">
      <div class="editor-nav">
        <div class="header-left">
          <router-link to="/admin/dashboard" class="btn btn-secondary">
            <i class="fas fa-arrow-left"></i>
            <span class="btn-text">Back to Dashboard</span>
          </router-link>
          <h1>{{ isEditing ? 'Edit Post' : 'Create New Post' }}</h1>
        </div>
        <div class="header-actions">
          <button 
            @click="saveAsDraft" 
            :disabled="saving || !title.trim()"
            class="btn btn-secondary"
          >
            <i class="fas fa-save"></i>
            <span class="btn-text">{{ saving ? 'Saving...' : 'Save Draft' }}</span>
          </button>
          <button 
            @click="saveAndPublish" 
            :disabled="saving || !title.trim() || !content.trim()"
            class="btn btn-primary"
          >
            <i class="fas fa-globe"></i>
            <span class="btn-text">{{ saving ? 'Publishing...' : 'Save & Publish' }}</span>
          </button>
        </div>
      </div>
    </header>

    <div v-if="error" class="error-message">
      {{ error }}
      <button @click="error = ''" class="btn-close">&times;</button>
    </div>

    <div v-if="success" class="success-message">
      {{ success }}
      <button @click="success = ''" class="btn-close">&times;</button>
    </div>

    <main class="editor-content">
      <div class="editor-form">
        <div class="form-row">
          <div class="form-group">
            <label for="title">Title *</label>
            <input
              type="text"
              id="title"
              v-model="title"
              @input="updateSlug"
              placeholder="Enter post title"
              :disabled="saving"
              required
            >
          </div>
          <div class="form-group">
            <label for="slug">Slug *</label>
            <input
              type="text"
              id="slug"
              v-model="slug"
              placeholder="post-url-slug"
              :disabled="saving"
              required
            >
          </div>
        </div>

        <div class="form-group">
          <label for="excerpt">Excerpt</label>
          <textarea
            id="excerpt"
            v-model="excerpt"
            placeholder="Brief description of the post (optional)"
            rows="3"
            :disabled="saving"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="tags">Tags</label>
          <input
            type="text"
            id="tags"
            v-model="tagsInput"
            placeholder="Enter tags separated by commas"
            :disabled="saving"
          >
          <small class="form-help">Separate tags with commas (e.g., javascript, vue, web development)</small>
        </div>

        <!-- AI Metadata Section -->
        <div class="form-group ai-metadata-group">
          <div class="ai-metadata-header" @click="aiSectionOpen = !aiSectionOpen">
            <label>
              <i class="fas fa-robot"></i>
              AI-Generated Metadata
              <span v-if="aiMetadata" class="badge-success">Generated</span>
            </label>
            <div class="ai-actions">
              <button
                v-if="isEditing && title.trim() && content.trim()"
                @click.stop="generateMetadata"
                :disabled="generatingAI"
                class="btn btn-primary btn-sm"
                type="button"
              >
                <i :class="['fas', generatingAI ? 'fa-spinner fa-spin' : 'fa-magic']"></i>
                <span>{{ generatingAI ? 'Generating...' : 'Generate Metadata' }}</span>
              </button>
              <button
                v-if="isEditing"
                @click.stop="openRefinementModal"
                class="btn btn-secondary btn-sm"
                type="button"
              >
                <i class="fas fa-sparkles"></i>
                <span>Refine Story</span>
              </button>
            </div>
          </div>

          <div v-if="aiSectionOpen" class="ai-metadata-content">
            <div v-if="aiError" class="ai-error">{{ aiError }}</div>

            <div v-if="aiMetadata">
              <!-- TL;DR Section -->
              <div class="ai-field">
                <label>TL;DR</label>
                <ul class="tldr-list">
                  <li v-for="(point, index) in aiMetadata.tldr" :key="index">
                    {{ point }}
                  </li>
                </ul>
              </div>

              <!-- Keywords Section -->
              <div class="ai-field">
                <label>Keywords</label>
                <div class="keywords-tags">
                  <span
                    v-for="keyword in aiMetadata.keywords"
                    :key="keyword"
                    class="keyword-tag"
                  >
                    {{ keyword }}
                  </span>
                </div>
              </div>

              <!-- Meta Description Section -->
              <div class="ai-field">
                <label>Meta Description</label>
                <p class="meta-description">{{ aiMetadata.meta_description }}</p>
              </div>

              <!-- Metadata Info -->
              <div class="ai-metadata-info">
                <small>
                  Generated {{ formatDate(aiMetadata.generated_at) }}
                  <span v-if="aiMetadata.processing_time_ms">
                    ({{ aiMetadata.processing_time_ms }}ms)
                  </span>
                </small>
              </div>
            </div>

            <div v-else class="ai-empty-state">
              <i class="fas fa-robot"></i>
              <p>No AI metadata generated yet</p>
              <small>Click "Generate AI Metadata" to create SEO-optimized content</small>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="content">Content *</label>
          <div ref="editorContainer" class="editor-container"></div>
        </div>
      </div>
    </main>

    <!-- Refinement Modal -->
    <RefinementModal
      :show="showRefinementModal"
      :post-id="postId"
      @close="closeRefinementModal"
      @apply-title="handleApplyRefinedTitle"
      @apply-excerpt="handleApplyRefinedExcerpt"
      @apply-content="handleApplyRefinedContent"
      @apply-tags="handleApplyRefinedTags"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useBlog } from '@/services/blog';
import RefinementModal from './RefinementModal.vue';
import EasyMDE from 'easymde';
import 'easymde/dist/easymde.min.css';

export default {
  name: 'PostEditor',
  components: {
    RefinementModal,
  },
  props: {
    id: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const { getPost, createPost, updatePost, generateAIMetadata } = useBlog();

    // Form data
    const title = ref('');
    const slug = ref('');
    const content = ref('');
    const excerpt = ref('');
    const tagsInput = ref('');
    const published = ref(false);

    // State
    const loading = ref(false);
    const saving = ref(false);
    const error = ref('');
    const success = ref('');
    const editor = ref(null);
    const editorContainer = ref(null);

    // AI Metadata state
    const aiMetadata = ref(null);
    const generatingAI = ref(false);
    const aiError = ref('');
    const aiSectionOpen = ref(false);

    // Refinement modal state
    const showRefinementModal = ref(false);

    // Computed
    const isEditing = computed(() => !!props.id || !!route.params.id);
    const postId = computed(() => props.id || route.params.id);
    
    // Helper functions
    const generateSlug = (text) => {
      return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
    };
    
    const updateSlug = () => {
      if (!isEditing.value) {
        slug.value = generateSlug(title.value);
      }
    };
    
    const parseTags = (tagsString) => {
      if (!tagsString) return [];
      return tagsString
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
    };
    
    const formatTags = (tagsArray) => {
      if (!tagsArray || tagsArray.length === 0) return '';
      return tagsArray.join(', ');
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // Editor setup
    const initializeEditor = () => {
      if (editor.value) {
        editor.value.toTextArea();
        editor.value = null;
      }
      
      if (editorContainer.value) {
        const textarea = document.createElement('textarea');
        textarea.value = content.value;
        editorContainer.value.appendChild(textarea);
        
        editor.value = new EasyMDE({
          element: textarea,
          placeholder: 'Write your post content in Markdown...',
          spellChecker: false,
          autofocus: false,
          toolbar: [
            'bold', 'italic', 'heading', '|',
            'quote', 'unordered-list', 'ordered-list', '|',
            'link', 'image', '|',
            'code', 'table', '|',
            'preview', 'side-by-side', 'fullscreen', '|',
            'guide'
          ],
          shortcuts: {
            togglePreview: 'Cmd-P',
            toggleSideBySide: null,
            toggleFullScreen: 'F11'
          },
          status: ['autosave', 'lines', 'words', 'cursor'],
          tabSize: 2,
        });
        
        editor.value.codemirror.on('change', () => {
          content.value = editor.value.value();
        });
      }
    };
    
    // Data loading
    const loadPost = async () => {
      if (!isEditing.value) return;
      
      loading.value = true;
      error.value = '';
      
      try {
        const post = await getPost(postId.value);
        title.value = post.title;
        slug.value = post.slug;
        content.value = post.content || '';
        excerpt.value = post.excerpt || '';
        tagsInput.value = formatTags(post.tags);
        published.value = post.published;

        // Load AI metadata if available
        if (post.tldr && post.tldr.length > 0) {
          aiMetadata.value = {
            tldr: post.tldr,
            keywords: post.ai_keywords || [],
            meta_description: post.ai_meta_description || '',
            generated_at: post.ai_generated_at
          };
        }

        // Update editor content
        if (editor.value) {
          editor.value.value(content.value);
        }
      } catch (err) {
        error.value = 'Failed to load post: ' + err.message;
      } finally {
        loading.value = false;
      }
    };
    
    // Save functions
    const savePost = async (shouldPublish = false) => {
      if (!title.value.trim()) {
        error.value = 'Title is required';
        return;
      }
      
      if (!slug.value.trim()) {
        error.value = 'Slug is required';
        return;
      }
      
      saving.value = true;
      error.value = '';
      success.value = '';
      
      try {
        const postData = {
          title: title.value.trim(),
          slug: slug.value.trim(),
          content: content.value,
          excerpt: excerpt.value.trim() || null,
          tags: parseTags(tagsInput.value),
          published: shouldPublish
        };
        
        let result;
        if (isEditing.value) {
          result = await updatePost(postId.value, postData);
        } else {
          result = await createPost(postData);
        }
        
        success.value = shouldPublish 
          ? 'Post saved and published successfully!'
          : 'Post saved as draft successfully!';
        
        // If creating new post, redirect to edit mode
        if (!isEditing.value && result.id) {
          setTimeout(() => {
            router.push({ name: 'EditPost', params: { id: result.id } });
          }, 1500);
        }
        
      } catch (err) {
        error.value = 'Failed to save post: ' + err.message;
      } finally {
        saving.value = false;
      }
    };
    
    const saveAsDraft = () => savePost(false);
    const saveAndPublish = () => savePost(true);

    // AI Metadata generation
    const generateMetadata = async () => {
      if (!isEditing.value || !postId.value) {
        aiError.value = 'Please save the post first';
        setTimeout(() => { aiError.value = ''; }, 3000);
        return;
      }

      generatingAI.value = true;
      aiError.value = '';

      try {
        const metadata = await generateAIMetadata(postId.value);

        aiMetadata.value = metadata;
        aiSectionOpen.value = true;
        success.value = 'AI metadata generated successfully!';

        // Clear success message after 3 seconds
        setTimeout(() => { success.value = ''; }, 3000);

      } catch (err) {
        aiError.value = 'Failed to generate metadata: ' + err.message;
      } finally {
        generatingAI.value = false;
      }
    };

    // Refinement modal handlers
    const openRefinementModal = () => {
      if (!isEditing.value || !postId.value) {
        error.value = 'Please save the post first before using refinement';
        setTimeout(() => { error.value = ''; }, 3000);
        return;
      }
      if (!content.value || content.value.trim().length < 100) {
        error.value = 'Post content must be at least 100 characters to refine';
        setTimeout(() => { error.value = ''; }, 3000);
        return;
      }
      showRefinementModal.value = true;
    };

    const closeRefinementModal = () => {
      showRefinementModal.value = false;
    };

    const handleApplyRefinedTitle = (newTitle) => {
      title.value = newTitle;
      success.value = 'Refined title applied successfully!';
      setTimeout(() => { success.value = ''; }, 3000);
    };

    const handleApplyRefinedExcerpt = (newExcerpt) => {
      excerpt.value = newExcerpt;
      success.value = 'Refined excerpt applied successfully!';
      setTimeout(() => { success.value = ''; }, 3000);
    };

    const handleApplyRefinedContent = (newContent) => {
      content.value = newContent;

      // Completely reinitialize the editor to avoid CodeMirror state issues
      if (editor.value) {
        // Destroy the current editor instance
        editor.value.toTextArea();
        editor.value = null;

        // Completely clean the editor container
        if (editorContainer.value) {
          editorContainer.value.innerHTML = '';
        }

        // Wait a tick for cleanup, then reinitialize with new content
        setTimeout(() => {
          initializeEditor();
        }, 50);
      }

      success.value = 'Refined content applied successfully!';
      setTimeout(() => { success.value = ''; }, 3000);
    };

    const handleApplyRefinedTags = (newTags) => {
      tagsInput.value = newTags.join(', ');
      success.value = 'Refined tags applied successfully!';
      setTimeout(() => { success.value = ''; }, 3000);
    };

    // Lifecycle
    onMounted(async () => {
      await loadPost();
      initializeEditor();
    });
    
    onUnmounted(() => {
      if (editor.value) {
        editor.value.toTextArea();
        editor.value = null;
      }
    });
    
    // Watch for route changes
    watch(() => route.params.id, () => {
      loadPost();
    });
    
    return {
      title,
      slug,
      content,
      excerpt,
      tagsInput,
      loading,
      saving,
      error,
      success,
      isEditing,
      postId,
      editorContainer,
      updateSlug,
      saveAsDraft,
      saveAndPublish,
      // AI Metadata
      aiMetadata,
      generatingAI,
      aiError,
      aiSectionOpen,
      generateMetadata,
      formatDate,
      // Refinement
      showRefinementModal,
      openRefinementModal,
      closeRefinementModal,
      handleApplyRefinedTitle,
      handleApplyRefinedExcerpt,
      handleApplyRefinedContent,
      handleApplyRefinedTags,
    };
  },
};
</script>

<style scoped>
.post-editor {
  min-height: 100vh;
  background: #f8f9fa;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1001;
  overflow-y: auto;
}

.editor-header {
  background: linear-gradient(135deg, #2c3e50, #34495e);
  color: #fff;
  padding: 0;
  border-bottom: 1px solid #e1e5e9;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1002;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  height: 4.5rem;
  display: flex;
  align-items: center;
}

.editor-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-left h1 {
  margin: 0;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.error-message, .success-message {
  position: fixed;
  top: 5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 600px;
  margin: 0;
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1003;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.error-message {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.success-message {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  margin-left: 1rem;
  opacity: 0.6;
}

.btn-close:hover {
  opacity: 1;
}

.editor-content {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 4.5rem;
}

.editor-form {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-width: 800px;
  margin: 0 auto;
}

.form-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 0;
  border-bottom: 1px solid #e1e5e9;
}

.form-row .form-group {
  border-bottom: none;
  border-right: 1px solid #e1e5e9;
}

.form-row .form-group:last-child {
  border-right: none;
}

.form-group {
  padding: 1.5rem;
  border-bottom: 1px solid #e1e5e9;
}

.form-group:last-child {
  border-bottom: none;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #fafafa;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1);
  background: white;
  transform: translateY(-1px);
}

.form-group input:disabled,
.form-group textarea:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.7;
}

.form-help {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #666;
}

.editor-container {
  min-height: 400px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
  transition: all 0.3s ease;
}

.editor-container:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1);
  background: white;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

/* AI Metadata Styles */
.ai-metadata-group {
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  overflow: hidden;
  margin: 0;
}

.ai-metadata-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  cursor: pointer;
  transition: background 0.2s;
}

.ai-metadata-header:hover {
  background: #e9ecef;
}

.ai-metadata-header label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  cursor: pointer;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ai-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.badge-success {
  background: #d1fae5;
  color: #065f46;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
}

.ai-metadata-content {
  padding: 1.5rem;
  background: white;
}

.ai-field {
  margin-bottom: 1.5rem;
}

.ai-field:last-child {
  margin-bottom: 0;
}

.ai-field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  text-transform: none;
  letter-spacing: normal;
}

.tldr-list {
  margin: 0;
  padding-left: 1.5rem;
}

.tldr-list li {
  margin-bottom: 0.5rem;
  color: #333;
}

.keywords-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.keyword-tag {
  background: #e3f2fd;
  color: #1565c0;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
}

.meta-description {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border-left: 3px solid #007bff;
  margin: 0;
  color: #333;
  line-height: 1.6;
}

.ai-metadata-info {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e1e5e9;
}

.ai-metadata-info small {
  color: #666;
}

.ai-empty-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.ai-empty-state i {
  font-size: 2rem;
  color: #ccc;
  margin-bottom: 0.5rem;
  display: block;
}

.ai-empty-state p {
  margin: 0.5rem 0;
  font-weight: 600;
}

.ai-empty-state small {
  color: #999;
}

.ai-error {
  background: #fee2e2;
  color: #dc2626;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .editor-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .header-actions {
    justify-content: center;
  }

  .editor-content {
    padding: 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-group {
    padding: 1rem;
  }
}
</style>

<style>
/* Global styles for EasyMDE */
.editor-container .EasyMDEContainer {
  background: white;
  border: none;
  border-radius: 8px;
  overflow: hidden;
}

.editor-container .EasyMDEContainer .editor-toolbar {
  border: none;
  border-bottom: 1px solid #e1e5e9;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.editor-container .EasyMDEContainer .CodeMirror {
  border: none;
  font-size: 15px;
  line-height: 1.7;
  min-height: 300px;
  background: white;
}

.editor-container .EasyMDEContainer .CodeMirror-focused {
  border: none;
  box-shadow: none;
}

.editor-container .EasyMDEContainer .editor-preview,
.editor-container .EasyMDEContainer .editor-preview-side {
  background: white;
  padding: 1rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  line-height: 1.6;
}

.editor-container .EasyMDEContainer .editor-preview h1,
.editor-container .EasyMDEContainer .editor-preview-side h1 {
  border-bottom: 1px solid #e1e5e9;
  padding-bottom: 0.5rem;
}

.editor-container .EasyMDEContainer .editor-preview code,
.editor-container .EasyMDEContainer .editor-preview-side code {
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.9em;
}

.editor-container .EasyMDEContainer .editor-preview pre,
.editor-container .EasyMDEContainer .editor-preview-side pre {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
}

.editor-container .EasyMDEContainer .editor-preview blockquote,
.editor-container .EasyMDEContainer .editor-preview-side blockquote {
  border-left: 4px solid #007bff;
  padding-left: 1rem;
  margin-left: 0;
  color: #666;
}

@media (max-width: 768px) {
  .editor-header {
    height: auto;
    min-height: 3.8rem;
  }

  .editor-nav {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    padding: 1rem;
  }

  .header-left {
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
  }

  .header-left h1 {
    font-size: 1.25rem;
    text-align: center;
  }

  .header-actions {
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .btn-text {
    display: none;
  }

  .btn {
    padding: 0.75rem;
    min-width: 44px;
    justify-content: center;
  }

  .editor-content {
    padding: 1rem;
    margin-top: 4rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-row .form-group {
    border-right: none;
    border-bottom: 1px solid #e1e5e9;
  }

  .form-row .form-group:last-child {
    border-bottom: none;
  }

  .form-group textarea {
    min-height: 200px;
  }
}

@media (max-width: 480px) {
  .editor-header {
    min-height: 3.5rem;
  }

  .editor-content {
    margin-top: 3.5rem;
    padding: 0.5rem;
  }

  .header-actions {
    gap: 0.25rem;
  }

  .btn {
    padding: 0.5rem;
    font-size: 0.875rem;
  }
}
</style>